/**
 * Device Types - Frontend
 * @description Device-related types matching backend DTOs
 */

import { ID, PaginationParams, Timestamp } from './api.types';

// Enums
export enum DeviceStatus {
  ACTIVE = 'Active',
  INACTIVE = 'Inactive',
}

// VehicleType is now a flexible string type
export type VehicleType = string;

export enum DeviceAccessType {
  MASTER = 'Master',
  OPERATOR = 'Operator',
  VIEWER = 'Viewer',
}

// Device Response DTO (what the API returns)
export interface Device {
  id: ID;
  serialNumber: string;
  alias?: string;
  description?: string;
  vehicle: {
    plate?: string;
    type?: VehicleType;
    dimensions?: {
      length?: number;
      height?: number;
      maxLoad?: number;
    };
  };
  versions?: {
    controller?: string;
    screen?: string;
    sensor?: string;
  };
  status: DeviceStatus;
  lastConnection?: string | null;
  lastOperation?: string | null;
  /** IANA TZ recorded when lastConnection was updated */
  lastConnectionTz?: string | null;
  /** IANA TZ recorded when lastOperation was updated */
  lastOperationTz?: string | null;
  /** Próxima secuencia de lectura esperada (expuesto por API; lectura al cargar / tras reinicio). */
  nextExpectedSequence?: number | null;
  metadata?: {
    createdAt?: Timestamp;
    createdBy?: string;
    updatedAt?: Timestamp;
    updatedBy?: string;
    version?: number;
    status?: string;
  };
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

// Device Create DTO (for creating devices)
export interface DeviceCreateRequest {
  serialNumber: string;
  alias?: string;
  description?: string;
  vehicle: {
    plate?: string;
    type: VehicleType;
    dimensions?: {
      length?: number;
      height?: number;
      maxLoad?: number;
    };
  };
  versions?: {
    controller?: string;
    screen?: string;
    sensor?: string;
  };
  status?: DeviceStatus;
}

// Device Update DTO (for updating devices)
export interface DeviceUpdateRequest {
  id: string;
  serialNumber?: string;
  alias?: string;
  description?: string;
  vehicle?: {
    plate?: string;
    type?: VehicleType;
    dimensions?: {
      length?: number;
      height?: number;
      maxLoad?: number;
    };
  };
  versions?: {
    controller?: string;
    screen?: string;
    sensor?: string;
  };
  status?: DeviceStatus;
}

export type DeviceFormData = Omit<Device, '_id'>;

export interface DeviceFilters {
  serialNumber?: string;
  plate?: string;
  type?: VehicleType;
  status?: DeviceStatus;
  sortBy?: keyof Device;
  sortOrder?: 'asc' | 'desc';
}

export interface DeviceQueryParams
  extends Omit<DeviceFilters, 'status' | 'sortBy'>,
    PaginationParams {
  status?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}
